import { createContext, useContext } from 'react';
import { useStore, type StoreApi } from 'zustand';

import type { ResearchTutorialSegment } from '@/constants/researchTutorial';
import {
  createResearchTutorialStore,
  type ResearchTutorialStore,
} from '@/services/stores/researchTutorialStore';

export type AdvanceInput = {
  segment: ResearchTutorialSegment;
  zone: number;
  isLastStep: boolean;
  handleNext: () => void;
  handleStop: () => void;
};

/**
 * What the tutorial's surfaces can ask of it. Every function keeps one identity
 * for the life of the Ask layout, so passing these around never re-renders a
 * consumer.
 */
export type ResearchTutorialActions = {
  /** The empty conversation is on screen and the tutorial may begin there. */
  requestStart: () => void;
  /** The empty conversation has left the screen. */
  cancelStart: () => void;
  /** A new conversation's first question went out. */
  sendStarted: (conversationId: string) => void;
  /** That send was accepted or refused. Only an accepted send counts the tutorial as seen. */
  sendSettled: (conversationId: string, accepted: boolean) => void;
  advance: (input: AdvanceInput) => void;
  skip: (segment: ResearchTutorialSegment, zone: number, handleStop: () => void) => void;
  /** Records what a step's action button does. Held outside the store, so it never renders. */
  setZoneAction: (key: string, action: (() => void) | undefined) => void;
  /** The driver put a segment on screen. */
  segmentShown: (segment: ResearchTutorialSegment) => void;
};

type ResearchTutorialContextValue = {
  store: StoreApi<ResearchTutorialStore>;
  actions: ResearchTutorialActions;
};

const noop = () => {};

const IDLE_ACTIONS: ResearchTutorialActions = {
  requestStart: noop,
  cancelStart: noop,
  sendStarted: noop,
  sendSettled: noop,
  advance: noop,
  skip: noop,
  setZoneAction: noop,
  segmentShown: noop,
};

/**
 * Stands in outside the Ask layout, where the tutorial never runs. It stays
 * idle because nothing outside the provider can begin it.
 */
const IDLE_STORE = createResearchTutorialStore();

export const ResearchTutorialContext = createContext<ResearchTutorialContextValue | null>(null);

export function useResearchTutorialActions(): ResearchTutorialActions {
  return useContext(ResearchTutorialContext)?.actions ?? IDLE_ACTIONS;
}

export function useResearchTutorialStoreApi(): StoreApi<ResearchTutorialStore> {
  return useContext(ResearchTutorialContext)?.store ?? IDLE_STORE;
}

/** Subscribes to one slice of the tutorial's state. Always pass a narrow selector. */
export function useResearchTutorialStore<Selected>(
  selector: (state: ResearchTutorialStore) => Selected
): Selected {
  return useStore(useResearchTutorialStoreApi(), selector);
}
